import { ProfileFilters } from '@/hooks/useProfileFilters';

export const getHeightLabel = (value: string): string => {
  const heightMap: Record<string, string> = {
    '150_155': '1.50m - 1.55m',
    '156_160': '1.56m - 1.60m',
    '161_165': '1.61m - 1.65m',
    '166_170': '1.66m - 1.70m',
    '171_175': '1.71m - 1.75m',
    '176_180': '1.76m - 1.80m',
    '181_185': '1.81m - 1.85m',
    '186_190': '1.86m - 1.90m',
    '191_plus': '1.91m+',
  };
  return heightMap[value] || value;
};

export const getBodyTypeLabel = (value: string): string => {
  const bodyTypeMap: Record<string, string> = {
    'slim': 'Magro',
    'athletic': 'Atlético',
    'average': 'Mediano',
    'muscular': 'Musculoso',
    'curvy': 'Curvilíneo',
    'plus_size': 'Plus Size',
  };
  return bodyTypeMap[value] || value;
};

export const getEyeColorLabel = (value: string): string => {
  const eyeColorMap: Record<string, string> = {
    'brown': 'Castanhos',
    'blue': 'Azuis',
    'green': 'Verdes',
    'hazel': 'Mel',
    'gray': 'Cinza',
    'black': 'Pretos',
  };
  return eyeColorMap[value] || value;
};

export const getHairColorLabel = (value: string): string => {
  const hairColorMap: Record<string, string> = {
    'black': 'Preto',
    'brown': 'Castanho',
    'blonde': 'Loiro',
    'red': 'Ruivo',
    'gray': 'Grisalho',
    'white': 'Branco',
    'other': 'Outro',
  };
  return hairColorMap[value] || value;
};

export const getSmokerLabel = (value: string): string => {
  const smokerMap: Record<string, string> = {
    'yes': 'Sim',
    'no': 'Não',
    'occasionally': 'Ocasionalmente',
  };
  return smokerMap[value] || value;
};

export const getDrinkerLabel = (value: string): string => {
  const drinkerMap: Record<string, string> = {
    'yes': 'Sim',
    'no': 'Não',
    'socially': 'Socialmente',
  };
  return drinkerMap[value] || value;
};

export const getTattoosLabel = (value: string): string => {
  const tattoosMap: Record<string, string> = {
    'none': 'Nenhuma',
    'few': 'Poucas',
    'many': 'Muitas',
  };
  return tattoosMap[value] || value;
};

export const getPiercingsLabel = (value: string): string => {
  const piercingsMap: Record<string, string> = {
    'none': 'Nenhum',
    'few': 'Poucos',
    'many': 'Muitos',
  };
  return piercingsMap[value] || value;
};

export const getMaritalStatusLabel = (value: string): string => {
  const maritalStatusMap: Record<string, string> = {
    'single': 'Solteiro(a)',
    'married': 'Casado(a)',
    'divorced': 'Divorciado(a)',
    'widowed': 'Viúvo(a)',
    'complicated': 'É complicado',
  };
  return maritalStatusMap[value] || value;
};

export const getGenderLabel = (value: string): string => {
  const genderMap: Record<string, string> = {
    'male': 'Masculino',
    'female': 'Feminino',
    'non_binary': 'Não-binário',
    'other': 'Outro',
  };
  return genderMap[value] || value;
};

export const getOrientationLabel = (value: string): string => {
  const orientationMap: Record<string, string> = {
    'straight': 'Hétero',
    'gay': 'Gay',
    'lesbian': 'Lésbica',
    'bisexual': 'Bissexual',
    'other': 'Outro',
  };
  return orientationMap[value] || value;
};

export const getRelationshipTypeLabel = (value: string): string => {
  const relationshipTypeMap: Record<string, string> = {
    'casual': 'Casual',
    'serious': 'Sério',
    'open': 'Aberto',
    'friends': 'Amigos',
  };
  return relationshipTypeMap[value] || value;
};

export interface ActiveFilter {
  category: string;
  label: string;
  values: string[];
}

export const formatActiveFilters = (filters: ProfileFilters): ActiveFilter[] => {
  const activeFilters: ActiveFilter[] = [];

  if (filters.gender && filters.gender.length > 0) {
    activeFilters.push({
      category: 'Gênero',
      label: 'Gênero',
      values: filters.gender.map(getGenderLabel),
    });
  }

  if (filters.orientation && filters.orientation.length > 0) {
    activeFilters.push({
      category: 'Orientação',
      label: 'Orientação',
      values: filters.orientation.map(getOrientationLabel),
    });
  }

  if (filters.minAge !== 18 || filters.maxAge !== 65) {
    activeFilters.push({
      category: 'Idade',
      label: 'Faixa etária',
      values: [`${filters.minAge || 18} - ${filters.maxAge || 65} anos`],
    });
  }

  if (filters.relationshipTypes && filters.relationshipTypes.length > 0) {
    activeFilters.push({
      category: 'Relacionamento',
      label: 'Tipo de relacionamento',
      values: filters.relationshipTypes.map(getRelationshipTypeLabel),
    });
  }

  if (filters.estadoCivil && filters.estadoCivil.length > 0) {
    activeFilters.push({
      category: 'Estado Civil',
      label: 'Estado civil',
      values: filters.estadoCivil.map(getMaritalStatusLabel),
    });
  }

  if (filters.alturas && filters.alturas.length > 0) {
    activeFilters.push({
      category: 'Altura',
      label: 'Altura',
      values: filters.alturas.map(getHeightLabel),
    });
  }

  if (filters.tiposCorpo && filters.tiposCorpo.length > 0) {
    activeFilters.push({
      category: 'Tipo de Corpo',
      label: 'Tipo de corpo',
      values: filters.tiposCorpo.map(getBodyTypeLabel),
    });
  }

  if (filters.coresOlhos && filters.coresOlhos.length > 0) {
    activeFilters.push({
      category: 'Cor dos Olhos',
      label: 'Cor dos olhos',
      values: filters.coresOlhos.map(getEyeColorLabel),
    });
  }

  if (filters.coresCabelo && filters.coresCabelo.length > 0) {
    activeFilters.push({
      category: 'Cor do Cabelo',
      label: 'Cor do cabelo',
      values: filters.coresCabelo.map(getHairColorLabel),
    });
  }

  if (filters.fumante && filters.fumante.length > 0) {
    activeFilters.push({
      category: 'Fumante',
      label: 'Fumante',
      values: filters.fumante.map(getSmokerLabel),
    });
  }

  if (filters.bebidas && filters.bebidas.length > 0) {
    activeFilters.push({
      category: 'Bebidas',
      label: 'Bebidas',
      values: filters.bebidas.map(getDrinkerLabel),
    });
  }

  if (filters.tatuagens && filters.tatuagens.length > 0) {
    activeFilters.push({
      category: 'Tatuagens',
      label: 'Tatuagens',
      values: filters.tatuagens.map(getTattoosLabel),
    });
  }

  if (filters.piercings && filters.piercings.length > 0) {
    activeFilters.push({
      category: 'Piercings',
      label: 'Piercings',
      values: filters.piercings.map(getPiercingsLabel),
    });
  }

  if (filters.interests && filters.interests.length > 0) {
    activeFilters.push({
      category: 'Interesses',
      label: 'Interesses',
      values: filters.interests,
    });
  }

  return activeFilters;
};
